/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button, Icon, IllustratedMessage } from '@adobe-commerce/elsie/components';
import { Cart } from '@adobe-commerce/elsie/icons';

import '@/cart/components/EmptyCart/EmptyCart.css';

export interface EmptyCartProps extends HTMLAttributes<HTMLDivElement> {
  ctaLinkURL?: string;
}

export const EmptyCart: FunctionComponent<EmptyCartProps> = ({
  className,
  children,
  ctaLinkURL,
  ...props
}) => {
  const labels = useText({
    emptyCart: 'Cart.EmptyCart.heading',
    cta: 'Cart.EmptyCart.cta',
  });

  return (
    <div {...props} className={classes(['cart-empty-cart', className])}>
      <IllustratedMessage
        className={classes(['cart-empty-cart__wrapper', className])}
        data-testid="cart-empty-cart"
        heading={labels.emptyCart}
        icon={<Icon className="cart-empty-cart__icon" source={Cart} />}
        action={
          ctaLinkURL ? (
            <Button
              data-testid="cart-empty-cart-button"
              size="medium"
              variant="primary"
              key="routeHome"
              type="submit"
              href={ctaLinkURL}
            >
              {labels.cta}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
};
